export function downloadJson(filename, value) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  downloadBlob(filename, blob);
}

export function downloadCsv(filename, value) {
  const blob = new Blob([value], { type: "text/csv;charset=utf-8" });
  downloadBlob(filename, blob);
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function timestampForFilename(date = new Date()) {
  return date.toISOString().replace(/[:.]/g, "-");
}

export function normalizeSensorMeta(sensorMeta) {
  if (!Array.isArray(sensorMeta)) return [];
  return sensorMeta
    .map((sensor) => ({
      index: Number(sensor?.index),
      name: String(sensor?.name ?? ""),
      adr: Number(sensor?.adr),
      dim: Number(sensor?.dim),
    }))
    .filter(
      (sensor) =>
        Number.isInteger(sensor.index) &&
        sensor.name &&
        Number.isInteger(sensor.adr) &&
        Number.isInteger(sensor.dim) &&
        sensor.adr >= 0 &&
        sensor.dim >= 0
    )
    .sort((a, b) => a.index - b.index);
}

export function buildSensorRecordingExport({ sensorMeta, samples, startedAt, stoppedAt }) {
  const sensors = normalizeSensorMeta(sensorMeta);
  const firstTime = samples[0]?.time ?? null;
  const lastTime = samples[samples.length - 1]?.time ?? null;
  const vectorLengths = getSampleVectorLengths(samples);
  const nsensordata = getSensordataLength(sensors, samples);

  return {
    format: "critterapp.mujoco.recording.v2",
    createdAt: new Date().toISOString(),
    startedAt,
    stoppedAt,
    model: {
      nsensor: sensors.length,
      nsensordata,
      sensors,
      nq: vectorLengths.qpos,
      nv: vectorLengths.qvel,
      nu: vectorLengths.ctrl,
    },
    recording: {
      sampleCount: samples.length,
      firstTime,
      lastTime,
      duration:
        Number.isFinite(firstTime) && Number.isFinite(lastTime)
          ? Math.max(0, lastTime - firstTime)
          : null,
      source: ["mjData.sensordata", "mjData.qpos", "mjData.qvel", "mjData.ctrl"],
    },
    samples,
  };
}

export function buildSensorRecordingCsv({ sensorMeta, samples }) {
  const sensors = normalizeSensorMeta(sensorMeta);
  const vectorLengths = getSampleVectorLengths(samples);
  const sensordataLength = getSensordataLength(sensors, samples);
  const columns = [
    { header: "time", read: (sample) => sample?.time },
    ...buildSensorColumns(sensors, sensordataLength),
    ...buildVectorColumns("qpos", vectorLengths.qpos),
    ...buildVectorColumns("qvel", vectorLengths.qvel),
    ...buildVectorColumns("ctrl", vectorLengths.ctrl),
  ];

  const rows = [
    columns.map((column) => csvCell(column.header)).join(","),
    ...samples.map((sample) => columns.map((column) => csvCell(column.read(sample))).join(",")),
  ];
  return `${rows.join("\n")}\n`;
}

function buildSensorColumns(sensors, sensordataLength) {
  if (sensors.length === 0) {
    return buildVectorColumns("sensordata", sensordataLength);
  }

  const columns = [];
  const usedHeaders = new Set();
  sensors.forEach((sensor) => {
    for (let offset = 0; offset < sensor.dim; offset += 1) {
      const index = sensor.adr + offset;
      const suffix = sensor.dim > 1 ? `[${offset}]` : "";
      const baseHeader = `sensor.${sensor.name}${suffix}`;
      const header = uniqueHeader(baseHeader, usedHeaders);
      columns.push({ header, read: (sample) => sample?.sensordata?.[index] });
    }
  });
  return columns;
}

function buildVectorColumns(key, length) {
  return Array.from({ length }, (_, index) => ({
    header: `${key}[${index}]`,
    read: (sample) => sample?.[key]?.[index],
  }));
}

function getSampleVectorLengths(samples) {
  return {
    qpos: maxArrayLength(samples, "qpos"),
    qvel: maxArrayLength(samples, "qvel"),
    ctrl: maxArrayLength(samples, "ctrl"),
  };
}

function getSensordataLength(sensors, samples) {
  return Math.max(
    sensors.reduce((max, sensor) => Math.max(max, sensor.adr + sensor.dim), 0),
    maxArrayLength(samples, "sensordata")
  );
}

function maxArrayLength(samples, key) {
  return samples.reduce(
    (max, sample) => Math.max(max, Array.isArray(sample?.[key]) ? sample[key].length : 0),
    0
  );
}

function uniqueHeader(baseHeader, usedHeaders) {
  let header = baseHeader;
  let suffix = 2;
  while (usedHeaders.has(header)) {
    header = `${baseHeader}_${suffix}`;
    suffix += 1;
  }
  usedHeaders.add(header);
  return header;
}

function csvCell(value) {
  if (value == null) return "";
  const text = Number.isFinite(value) ? String(value) : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
